import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-segmented-control.scss';

const Base = IdsKeyboardMixin(
  IdsLocaleMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

@customElement('ids-segmented-control')
@scss(styles)
export default class IdsSegmentedControl extends Base {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#addSegmentedClass();
    this.#attachEventHandlers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#detachEventHandlers();
  }

  static get attributes() {
    return [...super.attributes, attributes.COMPACT];
  }

  /**
   * @returns {boolean} whether compact mode is enabled
   */
  get compact(): boolean {
    return this.hasAttribute(attributes.COMPACT);
  }

  /**
   * @param {boolean} val enable/disable compact mode
   */
  set compact(val: boolean) {
    const isCompact = stringToBool(val);
    this.toggleAttribute(attributes.COMPACT, isCompact);
    this.#setCompactOnButtons(isCompact);
  }

  #setCompactOnButtons(isCompact: boolean) {
    this.querySelectorAll('ids-toggle-button').forEach((btn: any) => {
      btn.compact = isCompact;
    });
  }

  template(): string {
    return `<div class="ids-segmented-control" part="container">
      <slot></slot>
    </div>`;
  }

  /**
   * Adds the '.ids-toggle-button-segmented' class to each ids-toggle-button.
   */
  #addSegmentedClass() {
    const buttons = this.querySelectorAll('ids-toggle-button');
    buttons.forEach((button: any) => {
      button.classList.add('ids-toggle-button-segmented');
      if (this.compact) button.compact = true;
    });
  }

  /**
   * Attaches event handlers for managing the toggle button states.
   */
  #attachEventHandlers() {
    this.onEvent('click', this.container, (event: Event) => this.handleToggleClick(event));
    this.onEvent('keydown', this, (event: KeyboardEvent) => this.handleKeyDown(event));
    this.onEvent('pressed-changed', this, (event: CustomEvent) => {
      const { detail } = event;
      if (detail.pressed) {
        this.updateToggleState(detail.element);
      }
    });
  }

  /**
   * Override focus to delegate to the pressed button (or first button).
   * Required for toolbar keyboard navigation.
   */
  focus(): void {
    const buttons = Array.from(this.toggleButtons) as HTMLElement[];
    const pressed = buttons.find((b) => (b as any).pressed) ?? buttons[0];
    (pressed as HTMLElement)?.focus();
  }

  /**
   * Get the toggle buttons
   * @returns {HTMLElement} the toggle buttons
   */
  get toggleButtons() {
    return this.querySelectorAll('ids-toggle-button');
  }

  /**
   * Handles the toggle button click event.
   * @param {Event} event the click event
   */
  handleToggleClick(event: Event) {
    const target = event.target as HTMLElement;
    if (target.tagName.toLowerCase() === 'ids-toggle-button') {
      this.updateToggleState(target);
    }
  }

  /**
   * Handles keyboard navigation for the segmented control.
   * @param {KeyboardEvent} event the keyboard event
   */
  handleKeyDown(event: KeyboardEvent) {
    const { key } = event;
    const buttons = Array.from(this.toggleButtons) as HTMLElement[];

    if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(key)) {
      // Use event.target to find the focused button (more reliable than document.activeElement
      // since the toolbar may have already called stopPropagation before this handler runs)
      const target = event.target as HTMLElement;
      const currentIndex = buttons.findIndex((b) => b === target || b.contains(target));
      const fallbackIndex = buttons.findIndex((b) => (b as any).pressed);
      const activeIndex = currentIndex !== -1 ? currentIndex : fallbackIndex;
      if (activeIndex === -1) return;

      const isRTL = this.localeAPI?.isRTL();
      const isForward = key === 'ArrowDown' || (key === 'ArrowRight' && !isRTL) || (key === 'ArrowLeft' && isRTL);
      const isBackward = key === 'ArrowUp' || (key === 'ArrowLeft' && !isRTL) || (key === 'ArrowRight' && isRTL);

      const atEnd = isForward && activeIndex === buttons.length - 1;
      const atStart = isBackward && activeIndex === 0;

      if (atEnd || atStart) return;

      event.preventDefault();
      event.stopPropagation();

      const nextIndex = isForward ? activeIndex + 1 : activeIndex - 1;
      (buttons[nextIndex] as HTMLElement).focus();
    }

    // Space/Enter activates the focused button
    if (key === ' ' || key === 'Enter') {
      const target = event.target as HTMLElement;
      const focused = buttons.find((b) => b === target || b.contains(target));
      if (focused) {
        event.preventDefault();
        this.updateToggleState(focused);
      }
    }
  }

  /**
   * Updates the state of the toggle buttons, activating the pressed one and resetting others.
   * @param {HTMLElement} pressedButton the button that was pressed
   */
  updateToggleState(pressedButton: HTMLElement) {
    const buttons = this.toggleButtons;
    buttons.forEach((button: HTMLElement | any) => {
      if (button === pressedButton) {
        button.pressed = true;
      } else {
        button.pressed = false;
      }
    });
  }

  /**
   * Remove event handlers
   */
  #detachEventHandlers() {
    this.offEvent('click', this.container);
    this.offEvent('keydown', this);
    this.offEvent('pressed-changed', this);
  }
}
